<template>
  <div
    :class="[
      $style.base,
      { [$style.isSmall]: $screen === 's' || $screen === 'm' }
    ]"
  >
    <Flex
      direction="column"
      :gap="1"
      :class="$style.inner"
    >
      <Flex
        direction="column"
        :gap=".5"
      >
        <NoResults
          v-if="$screen === 'l'"
          :class="$style.noResults"
        />
        <BaseText>{{ $t('products.select_products') }}</BaseText>
      </Flex>
      <Flex
        direction="column"
        v-test="'autoButton'"
      >
        <BaseButton
          color="inverted"
          :loading="$apollo.loading"
          :disabled="autoStockOrderDisabled"
          @click="onCreateAuto"
          v-test="'onCreateAuto'"
        >
          {{ $t('products.create_automatically') }}
        </BaseButton>
        <BaseText
          v-if="!$apollo.loading"
          size="s"
          v-test="'noSetupExplained'"
        >
          {{ $t(
              `products.${autoStockOrderDisabled ? 'automatically_disabled' : 'automatically_explained'}`
            ) }}
        </BaseText>
      </Flex>
    </Flex>
  </div>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import { productFragment } from '@/graphql-fragments';
import { useLocationsStore } from '@/stores/locations';
import NoResults from '../NoResults.vue';
import type { SelectedProduct } from './types';

export default defineComponent({
  components: {
    NoResults,
  },
  props: {
    supplier: {
      type: Object,
      required: true,
    },
    products: {
      type: Array,
      required: false,
    },
  },
  emits: ['addSelectedProducts'],
  apollo: {
    products: {
      query: gql`
        query getOutOfStockProducts(
          $filter: String
          $locationId: ID
          $pagination: PaginationAttributes
          $categoryIds: [Int!]
          $supplierIds: [Int!]
        ) {
          products(
            filter: $filter
            locationId: $locationId
            pagination: $pagination
            categoryIds: $categoryIds
            supplierIds: $supplierIds
          ) {
            ...product
          }
        }
        ${productFragment}
      `,
      variables() {
        return {
          locationId: this.locationId,
          pagination: {
            currentPage: 1,
            perPage: 1000,
          },
          categoryIds: [],
          supplierIds: [this.supplier.id],
          filter: 'out_of_stock',
        };
      },
      fetchPolicy: 'no-cache',
    },
  },
  computed: {
    autoStockOrderDisabled() {
      return !this.products?.length;
    },
    ...mapState(useLocationsStore, ['locationId']),
  },
  methods: {
    onCreateAuto() {
      if (!this.products?.length) {
        return;
      }

      const selectedProducts: SelectedProduct[] = [];

      this.products.forEach((product) => {
        const mutableProduct = {
          ...product,
          id: undefined,
          productId: product.id,
        };

        if (mutableProduct.stockAmount !== undefined) {
          let targetStock;

          if (mutableProduct.stockMaximum !== undefined) {
            targetStock = mutableProduct.stockMaximum;
          } else {
            targetStock = mutableProduct.stockMinimum;
          }

          if (targetStock !== undefined) {
            mutableProduct.quantity = targetStock - mutableProduct.stockAmount;
            selectedProducts.push(mutableProduct);
          }
        }
      });

      this.$emit('addSelectedProducts', selectedProducts);
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: $spacing * 2;
}

.inner {
  max-width: 240px;
  text-align: center;
}

.noResults {
  margin-top: -$spacing * 3;
  margin-bottom: $spacing * 4;
}
</style>
